<script lang="ts">
	import {
		ButtonV2,
		DropdownMenu,
		IconButton,
		Modal,
		toast,
	} from '@Shelly/shelly-design-system/index';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { PROMOTIONS } from 'src/constants';
	import { save_home } from 'src/helpers/home_helpers';
	import { getPromoExpiryText, formatExpiryText } from 'src/helpers/promo_helpers';
	import { canManageHomeEntities } from 'src/helpers/houses/homes_helpers';
	import { postReq } from 'src/services/http';
	import { home_data, user_settings } from 'src/stores/account/user';
	import { network } from 'src/stores/session/native-bridge';
	import { homes } from 'src/stores/account/home';
	import { showGlobalLoader } from 'src/stores/ui/global-loader';
	import type { DiscountDetailsType, DiscountType } from 'src/types/DiscountType';
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import { modalStore } from 'src/stores/ui/modal_states';
	import { SmartHomeChallengeModalConfig } from 'src/configs/modals/SmartHomeChallengeModalConfig';
	import { ShellyBirthdayTeaserModalConfig } from 'src/configs/modals/ShellyBirthdayTeaserModalConfig';
	import { ShellyBirthdayCampaignModalConfig } from 'src/configs/modals/ShellyBirthdayCampaignModalConfig';
	import { BlackFridayPhase1Config } from 'src/configs/modals/BlackFridayPhase1Config';
	import { BlackFridayPhase2Config } from 'src/configs/modals/BlackFridayPhase2Config';
	import { energyPriceApi } from 'src/components/Consumption/VerivoxEnergyPriceIntegration/api/verivoxApi';
	import { isEnergyPriceFeatureEnabled } from 'src/helpers/energy_price_helpers';
	import { AnnualSurvey2026Config } from 'src/configs/modals/AnnualSurvey2026Config';
	import PromoDropdownHeader from './Promotions/molecules/PromoDropdownHeader.svelte';
	import PromoContentList from './Promotions/molecules/PromoContentList.svelte';
	import { Gen1ReplacementConfig } from 'src/configs/modals/Gen1ReplacementConfig';
	import { ThirtyMillionDevicesModalConfig } from 'src/configs/modals/ThirtyMillionDevicesModalConfig';
	import { XlX2FlashDealModalConfig } from 'src/configs/modals/XlX2FlashDealModalConfig';
	import { WallDisplayFlashSale2ModalConfig } from 'src/configs/modals/WallDisplayFlashSale2ModalConfig';
	import { SpringDeals2026ModalConfig } from 'src/configs/modals/SpringDeals2026ModalConfig';
	import { SmartHomeChallenge2026ModalConfig } from 'src/configs/modals/SmartHomeChallenge2026ModalConfig';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	// ============================================
	// Responsive state - modal on mobile/tablet, dropdown on desktop
	// ============================================

	let windowClientWidth: number;
	$: isMobile = windowClientWidth <= SystemScreenBreakpoints.MOBILE_MAX;
	$: isTablet = windowClientWidth <= SystemScreenBreakpoints.TABLET_MAX;

	$: shouldUseModal = isMobile || isTablet;

	$: isMainUnitAccount = canManageHomeEntities($homes.list);

	let discountsNotification = 0;
	let showPromosDropdown = false;
	let showPromosModal = false;

	/** Toggle between modal (mobile/tablet) and dropdown (desktop) */
	const onTogglePromosMenu = () => {
		if (shouldUseModal) {
			showPromosModal = !showPromosModal;
		} else {
			showPromosDropdown = !showPromosDropdown;
		}
	};

	/** Close the promos menu (both modal and dropdown) */
	const closePromosMenu = () => {
		showPromosDropdown = false;
		showPromosModal = false;
	};

	// Replace the showModal bindings with modalStore actions
	const showSmartHomeChallenge = () => {
		modalStore.showModal(SmartHomeChallengeModalConfig());
	};

	const showBirthdayTeaser = () => {
		modalStore.showModal(ShellyBirthdayTeaserModalConfig());
	};

	const showBirthdayCampaign = () => {
		modalStore.showModal(ShellyBirthdayCampaignModalConfig());
	};

	const showBlackFridayPhase1 = () => {
		modalStore.showModal(BlackFridayPhase1Config(true));
	};

	const showBlackFridayPhase2 = () => {
		modalStore.showModal(BlackFridayPhase2Config(true));
	};

	const showAnnualSurvey2026 = () => {
		modalStore.showModal(AnnualSurvey2026Config());
	};

	const showGen1Replacement = () => {
		modalStore.showModal(Gen1ReplacementConfig(true));
	};

	const showThirtyMillionDevices = () => {
		modalStore.showModal(ThirtyMillionDevicesModalConfig(true));
	};

	const showXlX2FlashDeal = () => {
		modalStore.showModal(XlX2FlashDealModalConfig(true));
	};

	const showWallDisplayFlashSale2 = () => {
		modalStore.showModal(WallDisplayFlashSale2ModalConfig(true));
	};

	const showSpringDeals2026 = () => {
		modalStore.showModal(SpringDeals2026ModalConfig(true));
	};

	const showSmartHomeChallenge2026 = () => {
		modalStore.showModal(SmartHomeChallenge2026ModalConfig(true));
	};

	$: discounts = (
		$user_settings?.aep?.nd
			? Object.values($user_settings?.aep?.nd).filter((discount: DiscountType) => !discount?.seen)
			: []
	) as DiscountType[];

	$: if (discounts.length > 0) {
		if ($home_data?.aep?.nd) {
			if (Object.keys($user_settings?.aep?.nd).length > Object.keys($home_data?.aep?.nd).length) {
				discountsNotification =
					Object.keys($user_settings?.aep?.nd).length -
					Object.keys($home_data?.aep?.nd).length;
			} else {
				discountsNotification = 0;
			}
		} else {
			discountsNotification = discounts.length;
		}
	} else {
		discountsNotification = 0;
	}

	$: if (
		new Date().getTime() >= PROMOTIONS.SPIN_WHEEL.START_DATE &&
		new Date().getTime() < PROMOTIONS.SPIN_WHEEL.END_DATE
	) {
		if (!$home_data?.promotions?.spinWheel) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: if (
		new Date().getTime() >= PROMOTIONS.SMART_HOME_CHALLENGE.START_DATE &&
		new Date().getTime() < PROMOTIONS.SMART_HOME_CHALLENGE.END_DATE
	) {
		if (!$home_data?.promotions?.smartHomeChallenge) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: if (
		new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_TEASER.START_DATE &&
		new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_TEASER.END_DATE
	) {
		if (!$home_data?.promotions?.shellyBirthdayTeaser) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: if (
		new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.START_DATE &&
		new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.END_DATE
	) {
		if (!$home_data?.promotions?.shellyBirthdayCampaign) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: if (
		new Date().getTime() >= PROMOTIONS.ANNUAL_SURVEY_2026.START_DATE &&
		new Date().getTime() < PROMOTIONS.ANNUAL_SURVEY_2026.END_DATE
	) {
		if (!$home_data?.promotions?.annualSurvey2026) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: if (
		new Date().getTime() >= PROMOTIONS.THIRTY_MILLION_DEVICES.START_DATE &&
		new Date().getTime() < PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE
	) {
		if (!$home_data?.promotions?.thirtyMillionDevices) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: if (
		(!!$home_data?.aep?.xlx2?.ts || import.meta.env.DEV) &&
		new Date().getTime() >= PROMOTIONS.XL_X2_FLASH_DEAL.START_DATE &&
		new Date().getTime() < PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE
	) {
		if (!$home_data?.promotions?.xlX2FlashDeal) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: if (
		(!!$home_data?.aep?.wdfs2?.code || import.meta.env.DEV) &&
		isMainUnitAccount &&
		new Date().getTime() >= PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.START_DATE &&
		new Date().getTime() < PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE
	) {
		if (!$home_data?.promotions?.wallDisplayFlashSale2) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: if (
		new Date().getTime() >= PROMOTIONS.SPRING_DEALS_2026.START_DATE &&
		new Date().getTime() < PROMOTIONS.SPRING_DEALS_2026.END_DATE
	) {
		if (!$home_data?.promotions?.springDeals2026) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: if (
		new Date().getTime() >= PROMOTIONS.SMART_HOME_CHALLENGE_2026.START_DATE &&
		new Date().getTime() < PROMOTIONS.SMART_HOME_CHALLENGE_2026.END_DATE
	) {
		if (!$home_data?.promotions?.smartHomeChallenge2026) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: shouldShowEnergyPriceAlert =
		isEnergyPriceFeatureEnabled() && energyPriceApi.getCachedLocation() === null;

	$: if (shouldShowEnergyPriceAlert) {
		if (!$home_data?.promotions?.energyPriceSetup) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	$: g1dItems = $home_data?.aep?.g1d
		? Object.entries($home_data.aep.g1d).map(([key, item]) => ({
				...(item as DiscountDetailsType),
				key,
			}))
		: [];

	$: if (g1dItems.filter((item) => !item.seen).length > 0) {
		discountsNotification += g1dItems.filter((item) => !item.seen).length;
	}

	$: g1dExpiryText =
		g1dItems.length > 0
			? $format(
					formatExpiryText(g1dItems[0]?.expiry)?.key,
					formatExpiryText(g1dItems[0]?.expiry)?.object,
				)
			: '';

	$: thirtyMillionExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE)?.object,
	);

	$: xlX2FlashDealExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE)?.object,
	);

	$: wallDisplayFlashSale2ExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE)?.object,
	);

	$: springDeals2026ExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.SPRING_DEALS_2026.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.SPRING_DEALS_2026.END_DATE)?.object,
	);

	$: smartHomeChallenge2026ExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.SMART_HOME_CHALLENGE_2026.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.SMART_HOME_CHALLENGE_2026.END_DATE)?.object,
	);

	const handlePromoTriggerClick = async () => {
		const now = new Date().getTime();
		const promotionsUpdates: Record<string, number> = {};

		if (
			now >= PROMOTIONS.BLACK_FRIDAY_PHASE_1.START_DATE &&
			now < PROMOTIONS.BLACK_FRIDAY_PHASE_1.END_DATE &&
			!$home_data?.promotions?.blackFridayPhase1
		) {
			promotionsUpdates.blackFridayPhase1 = now;
		}
		if (
			now >= PROMOTIONS.BLACK_FRIDAY_PHASE_2.START_DATE &&
			now < PROMOTIONS.BLACK_FRIDAY_PHASE_2.END_DATE &&
			!$home_data?.promotions?.blackFridayPhase2
		) {
			promotionsUpdates.blackFridayPhase2 = now;
		}
		if (
			now >= PROMOTIONS.SPIN_WHEEL.START_DATE &&
			now < PROMOTIONS.SPIN_WHEEL.END_DATE &&
			!$home_data?.promotions?.spinWheel
		) {
			promotionsUpdates.spinWheel = now;
		}
		if (
			now >= PROMOTIONS.SMART_HOME_CHALLENGE.START_DATE &&
			now < PROMOTIONS.SMART_HOME_CHALLENGE.END_DATE &&
			!$home_data?.promotions?.smartHomeChallenge
		) {
			promotionsUpdates.smartHomeChallenge = now;
		}
		if (
			now >= PROMOTIONS.SHELLY_BIRTHDAY_TEASER.START_DATE &&
			now < PROMOTIONS.SHELLY_BIRTHDAY_TEASER.END_DATE &&
			!$home_data?.promotions?.shellyBirthdayTeaser
		) {
			promotionsUpdates.shellyBirthdayTeaser = now;
		}
		if (
			now >= PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.START_DATE &&
			now < PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.END_DATE &&
			!$home_data?.promotions?.shellyBirthdayCampaign
		) {
			promotionsUpdates.shellyBirthdayCampaign = now;
		}
		if (
			now >= PROMOTIONS.ANNUAL_SURVEY_2026.START_DATE &&
			now < PROMOTIONS.ANNUAL_SURVEY_2026.END_DATE &&
			!$home_data?.promotions?.annualSurvey2026
		) {
			promotionsUpdates.annualSurvey2026 = now;
		}
		if (
			now >= PROMOTIONS.THIRTY_MILLION_DEVICES.START_DATE &&
			now < PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE &&
			!$home_data?.promotions?.thirtyMillionDevices
		) {
			promotionsUpdates.thirtyMillionDevices = now;
		}
		if (
			(!!$home_data?.aep?.xlx2?.ts || import.meta.env.DEV) &&
			now >= PROMOTIONS.XL_X2_FLASH_DEAL.START_DATE &&
			now < PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE &&
			!$home_data?.promotions?.xlX2FlashDeal
		) {
			promotionsUpdates.xlX2FlashDeal = now;
		}
		if (
			(!!$home_data?.aep?.wdfs2?.code || import.meta.env.DEV) &&
			isMainUnitAccount &&
			now >= PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.START_DATE &&
			now < PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE &&
			!$home_data?.promotions?.wallDisplayFlashSale2
		) {
			promotionsUpdates.wallDisplayFlashSale2 = now;
		}
		if (
			now >= PROMOTIONS.SPRING_DEALS_2026.START_DATE &&
			now < PROMOTIONS.SPRING_DEALS_2026.END_DATE &&
			!$home_data?.promotions?.springDeals2026
		) {
			promotionsUpdates.springDeals2026 = now;
		}
		if (
			now >= PROMOTIONS.SMART_HOME_CHALLENGE_2026.START_DATE &&
			now < PROMOTIONS.SMART_HOME_CHALLENGE_2026.END_DATE &&
			!$home_data?.promotions?.smartHomeChallenge2026
		) {
			promotionsUpdates.smartHomeChallenge2026 = now;
		}
		if (
			now >= PROMOTIONS.ENERGY_PRICE_SETUP.START_DATE &&
			now < PROMOTIONS.ENERGY_PRICE_SETUP.END_DATE &&
			!$home_data?.promotions?.energyPriceSetup
		) {
			promotionsUpdates.energyPriceSetup = now;
		}

		if (Object.keys(promotionsUpdates).length > 0) {
			save_home('promotions', {
				...$home_data?.promotions,
				...promotionsUpdates,
			});
		}
		// On mobile/tablet, toggle the modal (DropdownMenu handles its own toggle on desktop)
		onTogglePromosMenu();
		// Note: On desktop, DropdownMenu's bind:show handles the toggle automatically
		const discountEntries: [string, DiscountType][] = $user_settings?.aep?.nd
			? (Object.entries($user_settings?.aep?.nd) as [string, DiscountType][])
			: [];
		const results = await Promise.all(
			discountEntries?.map(async ([promotion, values]) => {
				if (values?.seen) return true;
				const res = await postReq('user/set_mt_seen', {
					scope: 'nd',
					trigger: promotion,
				});
				return res?.isok;
			}),
		);

		const allSuccessful = results.every((result) => result === true);

		if (!allSuccessful) {
			toast.open({ type: 'danger', title: $format('error_global') });
			return;
		}

		if (
			allSuccessful &&
			$user_settings?.aep &&
			(!$home_data?.aep || JSON.stringify($home_data?.aep) !== JSON.stringify($user_settings?.aep))
		) {
			const updatedAep = { ...$user_settings?.aep };
			if ($home_data?.aep?.g1d) {
				updatedAep.g1d = $home_data.aep.g1d;
			}
			if ($home_data?.aep?.thm) {
				updatedAep.thm = $home_data.aep.thm;
			}
			if ($home_data?.aep?.xlx2) {
				updatedAep.xlx2 = $home_data.aep.xlx2;
			}
			if ($home_data?.aep?.wdfs2) {
				updatedAep.wdfs2 = $home_data.aep.wdfs2;
			}
			if ($home_data?.aep?.sd) {
				updatedAep.sd = $home_data.aep.sd;
			}
			if ($home_data?.aep?.shc26) {
				updatedAep.shc26 = $home_data.aep.shc26;
			}
			save_home('aep', updatedAep);
		}
	};
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<!-- Promotions Button and Dropdown (Desktop) -->
<DropdownMenu width={400} maxWidth={400} bind:show={showPromosDropdown} removeOverlay>
	<IconButton
		slot="trigger"
		icon="fas fa-badge-percent"
		disabled={$network === 'offline'}
		id="promo-dropdown-trigger"
		aria-label={$format('button_special_offers', { default: 'Special offers' })}
		notification={!$showGlobalLoader &&
			discountsNotification > 0 && {
				type: 'danger',
				title: discountsNotification,
			}}
		on:click={handlePromoTriggerClick}
	/>
	<div
		class="promo-dropdown-container d-flex flex-direction-column gap-m pl-s-plus pr-s-plus pt-s-plus pb-s-plus"
	>
		<!-- Header -->
		<PromoDropdownHeader label={$format('label_special_offers', { default: 'Special offers' })} />

		<!-- Promo Items -->
		<PromoContentList
			{g1dItems}
			{g1dExpiryText}
			{thirtyMillionExpiryText}
			{xlX2FlashDealExpiryText}
			{wallDisplayFlashSale2ExpiryText}
			{springDeals2026ExpiryText}
			{smartHomeChallenge2026ExpiryText}
			{shouldShowEnergyPriceAlert}
			on:closeMenu={closePromosMenu}
			on:showGen1Replacement={showGen1Replacement}
			on:showThirtyMillionDevices={showThirtyMillionDevices}
			on:showXlX2FlashDeal={showXlX2FlashDeal}
			on:showWallDisplayFlashSale2={showWallDisplayFlashSale2}
			on:showSpringDeals2026={showSpringDeals2026}
			on:showSmartHomeChallenge={showSmartHomeChallenge}
			on:showSmartHomeChallenge2026={showSmartHomeChallenge2026}
			on:showBirthdayTeaser={showBirthdayTeaser}
			on:showBirthdayCampaign={showBirthdayCampaign}
			on:showBlackFridayPhase1={showBlackFridayPhase1}
			on:showBlackFridayPhase2={showBlackFridayPhase2}
			on:showAnnualSurvey2026={showAnnualSurvey2026}
		/>

		<!-- Bottom CTA -->
		<ButtonV2
			variant="primary"
			width="full"
			aria-label={$format('button_active_discounts', { default: 'Active discounts' })}
			on:click={() => {
				closePromosMenu();
				push($showNewLayout ? '/explore-shelly/discounts' : '/settings/discounts');
			}}
			icon="fas fa-percent"
		>
			{$format('button_active_discounts', { default: 'Active discounts' })}
		</ButtonV2>
	</div>
</DropdownMenu>

<!-- Promotions Modal (Mobile/Tablet only) -->

<Modal
	bind:showModal={showPromosModal}
	removeScrollPadding
	hideSlotHeader
	hideSlotHeaderActions
	hideTitle={isMobile}
	on:close={() => {
		showPromosModal = false;
	}}
	title={$format('label_special_offers', { default: 'Special offers' })}
>
	<svelte:fragment slot="content">
		<!-- Header -->
		{#if isMobile}
			<div class="pl-s-plus pr-s-plus">
				<PromoDropdownHeader
					label={$format('label_special_offers', { default: 'Special offers' })}
				/>
			</div>
		{/if}

		<!-- Promo Items -->
		<div class={`pl-s-plus pr-s-plus ${isMobile ? 'pt-m' : ''}`}>
			<PromoContentList
				{g1dItems}
				{g1dExpiryText}
				{thirtyMillionExpiryText}
				{xlX2FlashDealExpiryText}
				{wallDisplayFlashSale2ExpiryText}
				{springDeals2026ExpiryText}
				{smartHomeChallenge2026ExpiryText}
				{shouldShowEnergyPriceAlert}
				on:closeMenu={closePromosMenu}
				on:showGen1Replacement={showGen1Replacement}
				on:showThirtyMillionDevices={showThirtyMillionDevices}
				on:showXlX2FlashDeal={showXlX2FlashDeal}
				on:showWallDisplayFlashSale2={showWallDisplayFlashSale2}
				on:showSpringDeals2026={showSpringDeals2026}
				on:showSmartHomeChallenge={showSmartHomeChallenge}
				on:showSmartHomeChallenge2026={showSmartHomeChallenge2026}
				on:showBirthdayTeaser={showBirthdayTeaser}
				on:showBirthdayCampaign={showBirthdayCampaign}
				on:showBlackFridayPhase1={showBlackFridayPhase1}
				on:showBlackFridayPhase2={showBlackFridayPhase2}
				on:showAnnualSurvey2026={showAnnualSurvey2026}
			/>
		</div>
	</svelte:fragment>

	<svelte:fragment slot="footer">
		<div class="pl-xs pr-xs">
			<ButtonV2
				variant="primary"
				width="full"
				aria-label={$format('button_active_discounts', {
					default: 'Active discounts',
				})}
				on:click={() => {
					closePromosMenu();
					push($showNewLayout ? '/explore-shelly/discounts' : '/settings/discounts');
				}}
			>
				{$format('button_active_discounts', { default: 'Active discounts' })}
			</ButtonV2>
		</div>
	</svelte:fragment>
</Modal>

<style>
	:global(.promo-dropdown-cta) {
		border-radius: 200px !important;
	}
</style>
